import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { PvpIntent } from './connection';
import {
  createPvpTeamController,
  type PvpTeamController,
  type PvpTeamControllerDeps,
} from './team-controller';
import type { PvpTeamUiState } from './team-machine';

/**
 * React's view of the team controller, and nothing more.
 *
 * The same shape as `usePvpController` and for the same reasons: no reducer, no
 * `fetch`, no `WebSocket`, no operation id, no generation and no asynchronous
 * work of its own. Every one of those lives in `createPvpTeamController`, where
 * it can be tested against a failing bootstrap and a hidden tab without a
 * renderer.
 *
 * The dependencies are held in a REF so the controller's identity never depends
 * on them. It owns a socket, a pause lease and a hidden hunt, so rebuilding it as
 * a side effect of an unrelated render would drop the socket, orphan the lease
 * and leave the hunt hidden mid-match.
 */
export interface PvpTeamControllerHandle {
  readonly state: PvpTeamUiState;
  readonly open: () => void;
  readonly createLobby: () => void;
  readonly joinLobby: (code: string) => void;
  /** Freezes the roster. The server refuses anybody who does not own the lobby. */
  readonly launch: () => void;
  readonly resume: () => void;
  readonly codeEdited: () => void;
  readonly markReady: () => void;
  readonly sendIntent: (intent: PvpIntent) => void;
  readonly leave: () => void;
  readonly retryHuntRecovery: () => void;
}

export function useTeamPvpController(deps: PvpTeamControllerDeps): PvpTeamControllerHandle {
  /*
   * The newest dependencies, without making them part of the controller's
   * identity. Written during render on purpose: an effect would leave the first
   * frame after a change calling yesterday's callback.
   */
  const latest = useRef(deps);
  latest.current = deps;

  const controller = useMemo<PvpTeamController>(() => {
    const stable: PvpTeamControllerDeps = {
      createLobby: (signal) => latest.current.createLobby(signal),
      joinLobby: (code, signal) => latest.current.joinLobby(code, signal),
      readLobby: (signal) => latest.current.readLobby(signal),
      launchLobby: (signal) => latest.current.launchLobby(signal),
      leaveLobby: (signal) => latest.current.leaveLobby(signal),
      openConnection: () => latest.current.openConnection(),
      pollIntervalMs: latest.current.pollIntervalMs,
      schedule: (run, ms) => latest.current.schedule(run, ms),
      acquirePause: () => latest.current.acquirePause(),
      setHuntVisible: (visible) => {
        latest.current.setHuntVisible(visible);
      },
      clearHuntIntent: () => {
        latest.current.clearHuntIntent();
      },
      openArena: (participantId) => {
        latest.current.openArena(participantId);
      },
      publishProcs: (cuts) => {
        latest.current.publishProcs(cuts);
      },
      publishBlasts: (blasts) => {
        latest.current.publishBlasts(blasts);
      },
      publishBleedBursts: (bursts) => {
        latest.current.publishBleedBursts(bursts);
      },
      publishReflects: (reflects) => {
        latest.current.publishReflects(reflects);
      },
      publishAuthoritative: (state) => {
        latest.current.publishAuthoritative(state);
      },
      closeArena: () => {
        latest.current.closeArena();
      },
      intentSent: (seq, intent) => {
        latest.current.intentSent(seq, intent);
      },
      bootstrapHunt: (signal) => latest.current.bootstrapHunt(signal),
    };
    return createPvpTeamController(stable);
    // Built once per mount. Every dependency reaches it through the ref above.
  }, []);

  useEffect(() => {
    /*
     * The cleanup is the whole reason `dispose` is idempotent. StrictMode
     * mounts, cleans up and mounts again, so this runs against a controller a
     * second effect may already have replaced.
     */
    return () => {
      controller.dispose();
    };
  }, [controller]);

  const state = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => controller.subscribe(() => onStoreChange()),
      [controller],
    ),
    useCallback(() => controller.getState(), [controller]),
    useCallback(() => controller.getState(), [controller]),
  );

  return useMemo(
    () => ({
      state,
      open: () => {
        controller.open();
      },
      createLobby: () => {
        controller.createLobby();
      },
      joinLobby: (code: string) => {
        controller.joinLobby(code);
      },
      launch: () => {
        controller.launch();
      },
      resume: () => {
        controller.resume();
      },
      codeEdited: () => {
        controller.codeEdited();
      },
      markReady: () => {
        controller.markReady();
      },
      sendIntent: (intent: PvpIntent) => {
        controller.sendIntent(intent);
      },
      leave: () => {
        controller.leave();
      },
      retryHuntRecovery: () => {
        controller.retryHuntRecovery();
      },
    }),
    [controller, state],
  );
}
